// Pure (i18n-free) formatters and mappers shared by the automation operations
// panel and its extracted sections. Anything that needs `t` lives in
// ../composables/useAutomationOperationsFormatters.js instead.

export const extractError = error =>
  error?.response?.data?.error ||
  error?.response?.data?.retry_error ||
  error?.message;

export const formatDate = value => {
  if (!value) return '-';
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value));
};

export const formatPercent = value => {
  if (value === null || value === undefined) return '-';
  return `${(Number(value) * 100).toFixed(1)}%`;
};

export const formatDuration = value => {
  if (value === null || value === undefined) return '-';
  const seconds = Number(value);
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  return `${(seconds / 60).toFixed(1)}m`;
};

export const eventLabel = id => `#${id}`;

export const observedThresholdLabel = item =>
  `${item.observed}/${item.threshold}`;

export const destinationQueueTotal = destination =>
  Number(destination.pending_count || 0) +
  Number(destination.processing_count || 0);

export const destinationFailureTotal = destination =>
  Number(destination.failed_count || 0) +
  Number(destination.recent_failed_delivery_count || 0);

export const destinationRowState = destination => {
  if (
    Number(destination.dead_letter_count || 0) > 0 ||
    destinationFailureTotal(destination) > 0
  ) {
    return 'error';
  }
  if (
    Number(destination.expired_processing_count || 0) > 0 ||
    destinationQueueTotal(destination) > 0
  ) {
    return 'warning';
  }
  return 'default';
};

export const actionReceiptProviders = action =>
  (action.receipts?.providers || [])
    .map(item => `${item.value} ${item.count}`)
    .join(', ');

export const userLabel = user => user.email || user.name || `#${user.id}`;

export const usersLabel = users => (users || []).map(userLabel).join(', ');

export const ruleRowState = rule => {
  if (rule.slo?.status === 'unhealthy') return 'error';
  if (rule.slo?.status === 'degraded') return 'warning';
  return 'default';
};

export const labelColor = value =>
  ({
    healthy: 'teal',
    quiet: 'teal',
    closed: 'teal',
    close: 'teal',
    compensated: 'teal',
    skipped: 'teal',
    reprocess_requested: 'blue',
    retry_failed: 'ruby',
    pending: 'amber',
    acknowledged: 'blue',
    unhealthy: 'ruby',
    firing: 'ruby',
    critical: 'ruby',
    degraded: 'amber',
    warning: 'amber',
    no_data: 'slate',
    blocked: 'ruby',
    failed: 'ruby',
    quarantined: 'ruby',
    succeeded: 'teal',
    partially_succeeded: 'amber',
    running: 'blue',
    ready: 'teal',
    force_preview: 'amber',
    active: 'teal',
    paused: 'amber',
    aligned: 'teal',
    old_version_active: 'amber',
    conflict: 'ruby',
    incomplete: 'amber',
  })[value] || 'slate';

export const deadLetterState = deadLetter =>
  ({
    pending: 'warning',
    retry_failed: 'error',
    reprocess_requested: 'active',
    acknowledged: 'active',
    skipped: 'success',
    compensated: 'success',
    closed: 'success',
  })[deadLetter.status] || 'default';

export const cadenceEnrollmentState = enrollment => {
  if (enrollment.version?.old_version) return 'warning';
  if (enrollment.status === 'paused') return 'warning';
  if (enrollment.due) return 'active';
  return 'default';
};

export const cadenceVersionLabel = enrollment => {
  const version = enrollment.version || {};
  const versionNumber = version.version_number || version.id || '-';
  if (!version.old_version) return `v${versionNumber}`;

  const published = version.published_version_id || '-';
  return `v${versionNumber} -> #${published}`;
};

export const contactReference = enrollment =>
  `#${enrollment.contact?.id || enrollment.id}`;

export const nextExecutionLabel = execution =>
  [
    execution.cadence?.name,
    execution.contact?.label,
    formatDate(execution.next_wakeup_at),
  ]
    .filter(Boolean)
    .join(' · ');

export const actionKey = (id, actionName) => `${id}:${actionName}`;

export const cadenceActionKey = (id, actionName) =>
  `cadence:${id}:${actionName}`;

// Map the redaction-safe dead-letter payload into the LaisLedgerDrawer entry
// shape. The backend never returns raw arguments/backtrace/error_message
// (redaction_contract), so the drawer surfaces operational fields + the audit
// trail as attempts and the operational review_metadata through the payload
// viewer (still redacted).
export const buildDeadLetterEntry = record => {
  if (!record) return null;
  const metadata = record.review_metadata || {};
  return {
    status: record.status,
    provider: record.queue,
    destination: record.job_class,
    error: record.error_class,
    receipt: record.runbook_key,
    idempotencyKey: record.review_action,
    payload: {
      cause: {
        error_class: record.error_class,
        status: record.status,
        review_action: record.review_action,
        review_reason_present: Boolean(record.review_reason),
        retry_error_class: metadata.retry_error_class,
        retry_error_present: Boolean(metadata.retry_error),
      },
      review_metadata: metadata,
      redaction: record.redaction || null,
    },
    attempts: (record.audit_history || []).map(audit => ({
      at: audit.created_at,
      status: audit.action,
      error:
        audit.changes?.retry_error_class ||
        audit.changes?.status ||
        audit.comment,
    })),
  };
};

// Compose the run-status panel from the execution presenter payload. Unknown
// backend statuses fall back to the neutral tone inside LaisRunStatusPanel.
export const buildExecutionRunStatus = execution => {
  if (!execution) return null;
  const lease = execution.lease || {};
  const timestamps = execution.timestamps || {};
  const errorText = [execution.error_class, execution.error_kind]
    .filter(Boolean)
    .join(' · ');
  return {
    status: execution.status || '',
    startedAt: timestamps.started_at || null,
    finishedAt: timestamps.finished_at || null,
    attempt:
      execution.attempt === null || execution.attempt === undefined
        ? null
        : Number(execution.attempt),
    maxAttempts:
      execution.max_attempts === null || execution.max_attempts === undefined
        ? null
        : Number(execution.max_attempts),
    worker: lease.leased_by || '',
    lock: lease.lease_expires_at
      ? { expires_at: formatDate(lease.lease_expires_at) }
      : null,
    error: errorText,
    stale: lease.leased_by ? lease.active === false : false,
  };
};

// Redaction-safe operational output for the execution drill-down.
export const executionPayloadOf = execution => {
  if (!execution) return null;
  return (
    execution.redacted_output ||
    execution.redacted_input ||
    execution.metadata ||
    null
  );
};

export const selectedActionPayloadOf = action => {
  if (!action) return null;

  const latestAttempt = [...(action.attempts || [])]
    .reverse()
    .find(attempt => attempt.redacted_output || attempt.redacted_input);
  if (latestAttempt) {
    return latestAttempt.redacted_output || latestAttempt.redacted_input;
  }

  const latestReceipt = [...(action.receipts || [])]
    .reverse()
    .find(receipt => receipt.redacted_payload);
  if (latestReceipt) return latestReceipt.redacted_payload;

  const latestApproval = [...(action.approvals || [])]
    .reverse()
    .find(approval => approval.redacted_payload);
  if (latestApproval) return latestApproval.redacted_payload;

  return action.plan || null;
};

export const reconciliationRowsOf = result => {
  const payload = result || {};
  return [
    ['receipt_kind', payload.receipt_kind],
    ['observed_state', payload.observed_state],
    ['provider', payload.provider],
    ['effect_type', payload.effect_type],
    ['reused', payload.reused],
    ['audit_id', payload.audit_id],
  ].filter(
    ([, value]) => value !== null && value !== undefined && value !== ''
  );
};

export const executionVersionRowsOf = version => {
  const payload = version || {};
  return [
    ['version_number', payload.version_number],
    ['version_id', payload.id],
    ['status', payload.status],
    ['checksum', payload.checksum],
    ['published_at', formatDate(payload.published_at)],
    ['valid_from', formatDate(payload.valid_from)],
    ['valid_until', formatDate(payload.valid_until)],
  ].filter(
    ([, value]) => value !== null && value !== undefined && value !== ''
  );
};

export const executionPolicyRowsOf = execution => {
  const payload = execution || {};
  return [
    ['failure_policy', payload.failure_policy],
    ['match_mode', payload.match_mode],
    ['exclusive_group', payload.exclusive_group],
    ['aggregate_type', payload.aggregate_type],
    ['aggregate_id', payload.aggregate_id],
    ['trace_id', payload.trace_id],
    ['quarantine_reason', payload.quarantine_reason],
  ].filter(
    ([, value]) => value !== null && value !== undefined && value !== ''
  );
};
